import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@outschool/backpack";
import {
  ActivityCurriculum,
  ActivityStandard,
} from "@outschool/business-rules";
import { useTranslation } from "@outschool/localization";
import { useLanguageString } from "@outschool/ui-components-website";
import { Checkbox } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import * as Activity from "../../../../../shared/Activity";
import { useActivityString } from "../../../../../shared/hooks/useActivityString";
import { Filter } from "../Filter";
import { ListingChildFilterProps } from "./ListingFilters";

type BooleanFilterOption = {
  label: string;
  value: string;
};

type BooleanFilterProps = {
  label: string;
  filters: Array<string | BooleanFilterOption>;
  enabledFilters: Array<string>;
  setEnabledFilters: (checked: boolean, values: string) => void;
  isCuratedCategoryFilter?: boolean;
  showTopBorder?: boolean;
  hideFormat?: boolean;
  noPadding?: boolean;
};

const BooleanFilters = ({
  label,
  filters,
  enabledFilters,
  setEnabledFilters,
  isCuratedCategoryFilter = false,
  showTopBorder = true,
  hideFormat = false,
  noPadding = false,
}: BooleanFilterProps) => {
  return (
    <Filter
      sectionTitle={label}
      largeTitle={isCuratedCategoryFilter}
      showTopBorder={showTopBorder}
      usePadding={!noPadding}
    >
      <Box
        sx={{
          padding: noPadding
            ? "0px 24px 24px 0px"
            : hideFormat
            ? "12px 24px 0px 0px"
            : "12px 24px 24px 0px",
        }}
      >
        {filters
          .map((v: string | BooleanFilterOption): BooleanFilterOption => {
            if (typeof v === "string") {
              return {
                label: v,
                value: v,
              };
            }
            return v;
          })
          .map(({ label, value: values }) => (
            <Checkbox
              key={label}
              id={label}
              label={label}
              iconSize="1em"
              checked={enabledFilters.some(t => t === values)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const checked = e.target.checked;
                setEnabledFilters(checked, values);
              }}
              sx={{
                cursor: "pointer",
                marginRight: "small",
                marginBottom: "small",
              }}
            />
          ))}
      </Box>
    </Filter>
  );
};

export default function UncommonFilters({
  searchFilters,
  onChangeWithParam,
  isCuratedCategoryFilter = false,
  hideFormat = false,
  hideClassType = false,
  hideChatGroupInsideClassType = false,
  hideLanguage = false,
  hideEvaluation = true,
  hideCurriculums = false,
  hideStandards = false,
  withAccordions = false,
}: ListingChildFilterProps) {
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\UncommonFilters",
  );

  const enabledDeliveryFilters = searchFilters.delivery
    ? searchFilters.delivery.split(",")
    : [];

  const formatFilters = searchFilters.format
    ? searchFilters.format.split(",")
    : [];
  const languageFilters = searchFilters.languageOfInstruction
    ? searchFilters.languageOfInstruction.split(",")
    : [];

  const evaluationFilters = [
    searchFilters.hasHomework && "hasHomework",
    searchFilters.hasAssessment && "hasAssessment",
    searchFilters.hasGrades && "hasGrades",
  ].filter(Boolean) as string[];
  const curriculumFilters = searchFilters.curriculums
    ? Array.isArray(searchFilters.curriculums)
      ? searchFilters.curriculums
      : (searchFilters.curriculums as string).split(",")
    : [];
  const standardFilters = searchFilters.standards
    ? Array.isArray(searchFilters.standards)
      ? searchFilters.standards
      : (searchFilters.standards as string).split(",")
    : [];

  const isMobile = Screen.useIsMobile();
  const { Format, Delivery, evaluationOptions } = useActivityString();
  const { translatedLanguageFilters } = useLanguageString();
  const deliveryFilters = hideChatGroupInsideClassType
    ? []
    : Delivery.filter(
        option => option.value != Activity.ACTIVITY_DELIVERY.ONE_ON_ONE_CLASS,
      );

  const activeFormats = Format.filter(format => {
    return format.value !== "Flexible schedule";
  });

  const unCommonFilters = [
    {
      title: t("Format"),
      isShowing: !hideFormat,
      isFilterActive: !!enabledDeliveryFilters.length,
      numberOfOptionsApplied: enabledDeliveryFilters.length,
      component: (
        <Box
          sx={
            isMobile
              ? { marginBottom: 0 }
              : {
                  paddingRight: "1em",
                  borderRight: "1px solid",
                  borderColor: "#F0F0F0",
                }
          }
        >
          <BooleanFilters
            showTopBorder={!Boolean(withAccordions)}
            label={withAccordions ? "" : t("Format")}
            isCuratedCategoryFilter={isCuratedCategoryFilter}
            filters={deliveryFilters}
            enabledFilters={enabledDeliveryFilters}
            setEnabledFilters={(checked, values) => {
              const newValue = checked
                ? [...enabledDeliveryFilters, values]
                : _.without(enabledDeliveryFilters, values);
              // when searching, Format = delivery
              onChangeWithParam?.("delivery", newValue);
            }}
          />
        </Box>
      ),
    },
    {
      title: t("Live or async"),
      isFilterActive: !!formatFilters.length || !!enabledDeliveryFilters.length,
      numberOfOptionsApplied:
        formatFilters.length + enabledDeliveryFilters.length,
      resetFilters: () => {
        onChangeWithParam?.("format", null);
        onChangeWithParam?.("delivery", null);
      },
      isShowing: !hideClassType,
      component: (
        <>
          <BooleanFilters
            showTopBorder={!Boolean(withAccordions)}
            label={withAccordions ? "" : t("Live or async")}
            isCuratedCategoryFilter={isCuratedCategoryFilter}
            filters={activeFormats}
            hideFormat={hideFormat}
            enabledFilters={formatFilters}
            setEnabledFilters={(checked, values) => {
              const newValue = checked
                ? [...formatFilters, values]
                : _.without(formatFilters, values);
              onChangeWithParam?.("format", newValue);
            }}
          />
        </>
      ),
    },
    {
      title: t("Homework, assessments & grading"),
      isShowing: !hideEvaluation,
      isFilterActive: !!evaluationFilters.length,
      numberOfOptionsApplied: evaluationFilters.length,
      resetFilters: () => {
        onChangeWithParam?.("hasHomework", null);
        onChangeWithParam?.("hasAssessment", null);
        onChangeWithParam?.("hasGrades", null);
      },
      component: (
        <BooleanFilters
          showTopBorder={!Boolean(withAccordions)}
          label={withAccordions ? "" : t("Homework, assessments & grading")}
          filters={evaluationOptions}
          isCuratedCategoryFilter={isCuratedCategoryFilter}
          enabledFilters={evaluationFilters}
          setEnabledFilters={(checked, values) => {
            onChangeWithParam?.(values, checked ? ["true"] : null);
          }}
        />
      ),
    },
    {
      title: t("Curriculum"),
      isShowing: !hideCurriculums,
      isFilterActive: !!curriculumFilters.length,
      numberOfOptionsApplied: curriculumFilters.length,
      resetFilters: () => {
        onChangeWithParam?.("curriculums", null);
      },
      component: (
        <BooleanFilters
          showTopBorder={!Boolean(withAccordions)}
          label={withAccordions ? "" : t("Curriculum")}
          enabledFilters={curriculumFilters}
          setEnabledFilters={(checked, values) => {
            const newValue = checked
              ? [...curriculumFilters, values]
              : _.without(curriculumFilters, values);
            onChangeWithParam?.("curriculums", newValue);
          }}
          filters={[
            {
              label: t("College Board Advanced Placement (AP)"),
              value: ActivityCurriculum.CollegeBoardAdvancedPlacement,
            },
            {
              label: t("Orton-Gillingham"),
              value: ActivityCurriculum.OrtonGillingham,
            },
            {
              label: t("Institute for Excellence in Writing (IEW)"),
              value: ActivityCurriculum.IEW,
            },
            {
              label: t("Eureka Math"),
              value: ActivityCurriculum.EurekaMath,
            },
            {
              label: t("Saxon Math"),
              value: ActivityCurriculum.SaxtonMath,
            },
            {
              label: t("Singapore Dimensions Math"),
              value: ActivityCurriculum.SingaporeDimensionsMath,
            },
            {
              label: t("International Baccalaureate (IB)"),
              value: ActivityCurriculum.InternationalBaccalaureate,
            },
          ]}
        />
      ),
    },
    {
      title: t("Standards"),
      isShowing: !hideStandards,
      isFilterActive: !!standardFilters.length,
      numberOfOptionsApplied: standardFilters.length,
      resetFilters: () => {
        onChangeWithParam?.("standards", null);
      },
      component: (
        <BooleanFilters
          showTopBorder={!Boolean(withAccordions)}
          label={withAccordions ? "" : t("Standards")}
          enabledFilters={standardFilters}
          setEnabledFilters={(checked, values) => {
            const newValue = checked
              ? [...standardFilters, values]
              : _.without(standardFilters, values);
            onChangeWithParam?.("standards", newValue);
          }}
          filters={[
            {
              label: t("Common Core State Standards (CCSS)"),
              value: ActivityStandard.CCSS,
            },
            {
              label: t("Next Generation Science Standards (NGSS)"),
              value: ActivityStandard.NGSS,
            },
            {
              label: t("Advanced Placement (AP) Standards"),
              value: ActivityStandard.AP,
            },
            {
              label: t(
                "National Curriculum Standards for Social Studies (NCSS)",
              ),
              value: ActivityStandard.NCSS,
            },
          ]}
        />
      ),
    },
    {
      title: t("Language"),
      isShowing: !hideLanguage,
      isFilterActive: !!languageFilters.length,
      numberOfOptionsApplied: languageFilters.length,
      resetFilters: () => {
        onChangeWithParam?.("languageOfInstruction", null);
      },
      component: (
        <BooleanFilters
          showTopBorder={!Boolean(withAccordions)}
          label={withAccordions ? "" : t("Language")}
          isCuratedCategoryFilter={isCuratedCategoryFilter}
          filters={translatedLanguageFilters}
          enabledFilters={languageFilters}
          setEnabledFilters={(checked, values) => {
            const newValue = checked
              ? [...languageFilters, values]
              : _.without(languageFilters, values);
            // when searching, Format = delivery
            onChangeWithParam?.("languageOfInstruction", newValue);
          }}
        />
      ),
    },
  ];
  return (
    <>
      {withAccordions
        ? unCommonFilters
            .filter(({ isShowing }) => isShowing)
            .map(
              ({ title, component, numberOfOptionsApplied, resetFilters }) => (
                <Accordion key={title}>
                  <AccordionSummary>
                    <Typography>{title}</Typography>
                    <>
                      {!!numberOfOptionsApplied && (
                        <Typography
                          sx={(theme: $TSFixMe) => ({
                            color: theme.palette.primary.main,
                            marginLeft: 5,
                          })}
                          onClick={(event: $TSFixMe) => {
                            event.stopPropagation();
                            resetFilters && resetFilters();
                          }}
                        >
                          {t("Clear ({{count}})", {
                            count: numberOfOptionsApplied,
                          })}
                        </Typography>
                      )}
                    </>
                  </AccordionSummary>
                  <AccordionDetails>{component}</AccordionDetails>
                </Accordion>
              ),
            )
        : unCommonFilters
            .filter(({ isShowing }) => isShowing)
            .map(({ title, component }) => (
              <Box
                flex
                sx={{
                  flexDirection: "column",
                  paddingBottom: "0.5em",
                }}
              >
                <Box flex sx={isMobile ? { flexDirection: "column" } : null}>
                  <Box key={title}>{component}</Box>
                </Box>
              </Box>
            ))}
    </>
  );
}
